import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NormalHeader from '../components/NormalHeader';
import NormalFooter from '../components/NormalFooter';
import { useNormalReservation } from '../context/NormalReservationContext';
import { reservationApi } from '../../services/api';

const NormalConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const {
    clinic,
    dateTime,
    personalInfo,
    setReservationNumber,
    setLoading,
    setError,
  } = useNormalReservation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!clinic || !dateTime) {
      navigate('/form/normal/form1');
    }
    if (!personalInfo.lastName || !personalInfo.firstName) {
      navigate('/form/normal/form2');
    }
  }, [clinic, dateTime, personalInfo, navigate]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日(${weekdays[date.getDay()]})`;
  };

  const formatTime = (timeStr: string) => timeStr.replace(':', '時') + '分';

  const handleSubmit = async () => {
    if (!clinic || !dateTime) {
      setSubmitError('予約情報が不完全です。');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setLoading(true);
    try {
      const response = await reservationApi.create({
        clinicId: clinic._id,
        dateTime,
        personalInfo,
      });
      setReservationNumber(response.reservationNumber);
      navigate('/form/normal/thanks');
    } catch (err) {
      setSubmitError('予約の登録に失敗しました。時間をおいて再度お試しください。');
      setError('予約登録に失敗しました');
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <NormalHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10 sm:px-6">
        <div className="mb-6">
          <p className="text-sm font-semibold text-brand-primary">Step 3</p>
          <h1 className="text-2xl font-bold text-gray-900">入力内容の確認</h1>
          <p className="text-sm text-gray-600">以下の内容で予約します。確認後、確定してください。</p>
        </div>

        <div className="space-y-6 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          {submitError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</p>}

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-800">クリニック</h2>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-900">{clinic?.name}</p>
              <p className="text-xs text-gray-600">{clinic?.address}</p>
              <p className="text-xs text-gray-600">{clinic?.phone}</p>
            </div>
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-800">予約日時</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
                <p className="text-xs text-gray-500">日付</p>
                <p className="font-semibold text-gray-900">{dateTime ? formatDate(dateTime.date) : ''}</p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
                <p className="text-xs text-gray-500">時間</p>
                <p className="font-semibold text-gray-900">{dateTime ? formatTime(dateTime.time) : ''}</p>
              </div>
            </div>
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-800">個人情報</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
                <p className="text-xs text-gray-500">お名前</p>
                <p className="font-semibold text-gray-900">
                  {personalInfo.lastName} {personalInfo.firstName}
                </p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
                <p className="text-xs text-gray-500">ふりがな</p>
                <p className="font-semibold text-gray-900">
                  {personalInfo.lastNameKana} {personalInfo.firstNameKana}
                </p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
                <p className="text-xs text-gray-500">電話番号</p>
                <p className="font-semibold text-gray-900">{personalInfo.phone}</p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
                <p className="text-xs text-gray-500">メールアドレス</p>
                <p className="font-semibold text-gray-900">{personalInfo.email}</p>
              </div>
              {personalInfo.request && (
                <div className="sm:col-span-2 rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
                  <p className="text-xs text-gray-500">ご要望・ご質問</p>
                  <p className="font-semibold text-gray-900">{personalInfo.request}</p>
                </div>
              )}
            </div>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={() => navigate('/form/normal/form2')}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50"
              disabled={submitting}
            >
              戻る
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow hover:bg-green-600 disabled:opacity-60"
            >
              {submitting ? '送信中...' : '予約を確定する'}
            </button>
          </div>
        </div>
      </main>
      <NormalFooter />
    </div>
  );
};

export default NormalConfirmation;
